import { useState, useEffect } from "react";
import { ChevronDown, Plus, Users, Lock, Globe, Copy, Check, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { PublicPondsDialog } from "./PublicPondsDialog";
import { ParentPondPicker } from "./ParentPondPicker";

interface Pond {
  id: string;
  name: string;
  type: string;
  isPublic: boolean;
  inviteCode: string | null;
  isOwner: boolean;
  parentId: string | null;
  requiresApproval: boolean;
  notifyOnJoin?: boolean;
}

interface PondSelectorProps {
  ponds: Pond[];
  currentPond: Pond | undefined;
  onSelectPond: (pondId: string) => void;
  onCreatePond: (name: string, isPublic: boolean, parentId?: string | null, requiresApproval?: boolean, notifyOnJoin?: boolean) => Promise<any>;
  onJoinPond: (inviteCode: string) => Promise<boolean>;
  onSearchPublicPonds: (searchTerm: string) => Promise<any[]>;
  showAsDropdown?: boolean;
  defaultOpenCreate?: boolean;
}

export const PondSelector = ({
  ponds,
  currentPond,
  onSelectPond,
  onCreatePond,
  onJoinPond,
  onSearchPublicPonds,
  showAsDropdown = true,
  defaultOpenCreate = false,
}: PondSelectorProps) => {
  const [createDialogOpen, setCreateDialogOpen] = useState(defaultOpenCreate);
  const [joinDialogOpen, setJoinDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [publicPondsOpen, setPublicPondsOpen] = useState(false);
  const [newPondName, setNewPondName] = useState("");
  const [isPublic, setIsPublic] = useState(false);
  const [requiresApproval, setRequiresApproval] = useState(true);
  const [notifyOnJoin, setNotifyOnJoin] = useState(false);
  const [parentPondId, setParentPondId] = useState<string | null>(null);
  const [inviteCode, setInviteCode] = useState("");
  const [copied, setCopied] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleCreatePond = async () => {
    if (!newPondName.trim()) return;
    setIsLoading(true);
    await onCreatePond(newPondName.trim(), isPublic, parentPondId, requiresApproval, notifyOnJoin);
    setNewPondName("");
    setIsPublic(false);
    setRequiresApproval(true);
    setNotifyOnJoin(false);
    setParentPondId(null);
    setCreateDialogOpen(false);
    setIsLoading(false);
  };

  const handleJoinPond = async () => {
    if (!inviteCode.trim()) return;
    setIsLoading(true);
    const success = await onJoinPond(inviteCode.trim());
    if (success) {
      setInviteCode("");
      setJoinDialogOpen(false);
    }
    setIsLoading(false);
  };

  const copyInviteCode = () => {
    if (currentPond?.inviteCode) {
      navigator.clipboard.writeText(currentPond.inviteCode);
      setCopied(true);
      toast.success("Código copiado!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Sync with defaultOpenCreate prop
  useEffect(() => {
    if (defaultOpenCreate) {
      setCreateDialogOpen(true);
    }
  }, [defaultOpenCreate]);

  // If not showing as dropdown, just render dialogs
  if (!showAsDropdown) {
    return (
      <>
        {/* Create Pond Dialog */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent className="sm:max-w-md p-6">
            <DialogHeader className="pb-4">
              <DialogTitle className="flex items-center gap-2">
                <span>🌿</span> Criar Nova Lagoa
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="pond-name-standalone">Nome da lagoa</Label>
                <Input
                  id="pond-name-standalone"
                  placeholder="Ex: Equipe de Vendas, Clube de Corrida..."
                  value={newPondName}
                  onChange={(e) => setNewPondName(e.target.value)}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Lagoa pai (opcional)</Label>
                <ParentPondPicker
                  ponds={ponds}
                  value={parentPondId}
                  onChange={setParentPondId}
                  placeholder="Nenhuma (lagoa raiz)"
                />
                <p className="text-xs text-muted-foreground">
                  Membros da lagoa pai terão acesso automaticamente
                </p>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="public-standalone">Lagoa pública</Label>
                  <p className="text-xs text-muted-foreground">
                    Qualquer pessoa pode buscar e encontrar
                  </p>
                </div>
                <Switch id="public-standalone" checked={isPublic} onCheckedChange={setIsPublic} />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="approval-standalone">Requer aprovação</Label>
                  <p className="text-xs text-muted-foreground">
                    Dono precisa aprovar novas entradas
                  </p>
                </div>
                <Switch id="approval-standalone" checked={requiresApproval} onCheckedChange={setRequiresApproval} />
              </div>

              {isPublic && !requiresApproval && (
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="notify-standalone">Notificar ao entrar</Label>
                    <p className="text-xs text-muted-foreground">
                      Receba notificação quando alguém entrar
                    </p>
                  </div>
                  <Switch id="notify-standalone" checked={notifyOnJoin} onCheckedChange={setNotifyOnJoin} />
                </div>
              )}

              <Button
                variant="coral"
                className="w-full"
                onClick={handleCreatePond}
                disabled={isLoading || !newPondName.trim()}
              >
                Criar Lagoa 🌿
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="gap-2 font-display h-10 px-3">
            {currentPond?.type === "private" ? (
              <Lock className="h-4 w-4" />
            ) : (
              <Users className="h-4 w-4" />
            )}
            <span className="max-w-[120px] truncate">
              {currentPond?.name || "Selecionar Lagoa"}
            </span>
            <ChevronDown className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56 mx-2">
          {ponds.map((pond) => (
            <DropdownMenuItem
              key={pond.id}
              onClick={() => onSelectPond(pond.id)}
              className="gap-2"
            >
              {pond.type === "private" ? (
                <Lock className="h-4 w-4" />
              ) : pond.isPublic ? (
                <Globe className="h-4 w-4" />
              ) : (
                <Users className="h-4 w-4" />
              )}
              {pond.name}
              {!pond.isOwner && (
                <span className="text-xs text-muted-foreground ml-auto">membro</span>
              )}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => setCreateDialogOpen(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            Criar nova lagoa
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setJoinDialogOpen(true)} className="gap-2">
            <Users className="h-4 w-4" />
            Entrar com código
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setPublicPondsOpen(true)} className="gap-2">
            <Search className="h-4 w-4" />
            Explorar lagoas públicas
          </DropdownMenuItem>
          {currentPond?.type === "shared" && currentPond.isOwner && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setShareDialogOpen(true)} className="gap-2">
                <Copy className="h-4 w-4" />
                Compartilhar lagoa
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Create Pond Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="sm:max-w-md p-6">
          <DialogHeader className="pb-4">
            <DialogTitle className="flex items-center gap-2">
              <span>🌿</span> Criar Nova Lagoa
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="pond-name">Nome da lagoa</Label>
              <Input
                id="pond-name"
                placeholder="Ex: Equipe de Vendas, Clube de Corrida..."
                value={newPondName}
                onChange={(e) => setNewPondName(e.target.value)}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Lagoa pai (opcional)</Label>
              <ParentPondPicker
                ponds={ponds}
                value={parentPondId}
                onChange={setParentPondId}
                placeholder="Nenhuma (lagoa raiz)"
              />
              <p className="text-xs text-muted-foreground">
                Membros da lagoa pai terão acesso automaticamente
              </p>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="public">Lagoa pública</Label>
                <p className="text-xs text-muted-foreground">
                  Qualquer pessoa pode buscar e encontrar
                </p>
              </div>
              <Switch id="public" checked={isPublic} onCheckedChange={setIsPublic} />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="approval">Requer aprovação</Label>
                <p className="text-xs text-muted-foreground">
                  Dono precisa aprovar novas entradas
                </p>
              </div>
              <Switch id="approval" checked={requiresApproval} onCheckedChange={setRequiresApproval} />
            </div>

            {isPublic && !requiresApproval && (
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="notify">Notificar ao entrar</Label>
                  <p className="text-xs text-muted-foreground">
                    Receba notificação quando alguém entrar
                  </p>
                </div>
                <Switch id="notify" checked={notifyOnJoin} onCheckedChange={setNotifyOnJoin} />
              </div>
            )}

            <Button
              variant="coral"
              className="w-full"
              onClick={handleCreatePond}
              disabled={isLoading || !newPondName.trim()}
            >
              Criar Lagoa 🌿
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Join Pond Dialog */}
      <Dialog open={joinDialogOpen} onOpenChange={setJoinDialogOpen}>
        <DialogContent className="sm:max-w-md p-6">
          <DialogHeader className="pb-4">
            <DialogTitle className="flex items-center gap-2">
              <span>🐸</span> Entrar em uma Lagoa
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5">{/* Código de convite */}
            <div className="space-y-2">
              <Label htmlFor="invite-code">Código de convite</Label>
              <Input
                id="invite-code"
                placeholder="Ex: ABC123XY"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                className="uppercase"
              />
            </div>
            <Button
              variant="coral"
              className="w-full"
              onClick={handleJoinPond}
              disabled={isLoading || !inviteCode.trim()}
            >
              Entrar na Lagoa 🐸
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Share Pond Dialog */}
      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent className="sm:max-w-md p-6">
          <DialogHeader className="pb-4">
            <DialogTitle className="flex items-center gap-2">
              <span>📤</span> Compartilhar Lagoa
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5">{/* Descrição */}
            <p className="text-sm text-muted-foreground">
              Compartilhe este código com seus amigos para que eles possam entrar na sua lagoa:
            </p>
            <div className="flex gap-2">
              <Input
                value={currentPond?.inviteCode || ""}
                readOnly
                className="font-mono text-lg text-center tracking-widest"
              />
              <Button variant="outline" size="icon" onClick={copyInviteCode}>
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <PublicPondsDialog
        open={publicPondsOpen}
        onOpenChange={setPublicPondsOpen}
        onSearch={onSearchPublicPonds}
        onJoinPond={async (pondId) => {
          // Direct join for public ponds
          toast.success("Você entrou na lagoa! 🐸");
        }}
      />
    </>
  );
};
